import { format } from "date-fns";
import { Typography } from "@/components/atoms";
import { clsx } from "@/utils";
import { formatFlightTime } from "./flightPickup";

/** The arrival + delay a pickup was built from, stated beside it as `arrival + delay = pickup`. */
export interface PickupWorking {
  /** "Estimated arrival", "Scheduled arrival" — the caption over the arrival cell. */
  arrivalLabel: string;
  /** The arrival, as an ISO carrying the arrival airport's offset. */
  arrivalIso: string;
  delayMinutes: number;
}

const Cell = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div
    className={clsx(
      "flex min-w-0 flex-1 flex-col justify-between self-stretch rounded-lg px-2.5 py-2",
      highlight ? "bg-primary-light" : "bg-neutral-surface-gray"
    )}
  >
    <Typography variant="caption" className="text-neutral-dark-gray">
      {label}
    </Typography>
    <Typography variant="action" className={highlight ? "text-primary-dark" : "text-neutral-black"}>
      {value}
    </Typography>
  </div>
);

/**
 * The pickup a flight booking is committing, and how it was worked out (RM-Client 7121:18
 * "Flight & Pickup / Pickup Time Card"): "Pickup time 5:05 PM / Wed 14 Oct 2026" over
 * `Estimated arrival 4:45 PM + Delay arrival 20 min = Pickup 5:05 PM`.
 *
 * `pickupAt` is in the commit convention — its BROWSER-LOCAL fields hold the booking location's wall
 * clock — so it is formatted with plain `date-fns`. The arrival is an airport-offset ISO and goes through
 * `formatFlightTime`. The caller hands the pickup from `resolvePickup`, the same call Proceed commits from,
 * and passes a `working` only when it reconciles with that pickup: a sum that does not add up is worse
 * than none (D11a).
 *
 * `tone="cancelled"` draws the error stroke the design uses for a cancelled flight.
 */
export const PickupTimeCard = ({
  pickupAt,
  working,
  tone = "default",
}: {
  pickupAt: Date;
  working?: PickupWorking | null;
  tone?: "default" | "cancelled";
}) => (
  <div
    data-testid="pickup-time-card"
    className={clsx(
      "flex flex-col gap-3 rounded-lg bg-white p-3.5",
      tone === "cancelled" ? "border-[1.5px] border-danger" : "border border-neutral-mid-gray"
    )}
  >
    <div className="flex flex-col gap-0.5">
      <Typography variant="caption" className="text-neutral-dark-gray">
        Pickup time
      </Typography>
      <Typography variant="title" className="!text-2xl !font-bold leading-8 text-neutral-black">
        {format(pickupAt, "h:mm a")}
      </Typography>
      {/* The date earns its place even though the flight card carries one: a late enough arrival pushes
          the pickup onto the FOLLOWING day, which is exactly when a reader needs to be told. */}
      <Typography variant="paragraph" className="text-neutral-silver-gray">
        {format(pickupAt, "EEE d MMM yyyy")}
      </Typography>
    </div>
    {working && (
      <div className="flex items-center gap-1.5">
        <Cell label={working.arrivalLabel} value={formatFlightTime(working.arrivalIso)} />
        <Typography variant="paragraph" aria-hidden className="text-neutral-silver-gray">
          +
        </Typography>
        <Cell label="Delay arrival" value={`${working.delayMinutes} min`} />
        <Typography variant="paragraph" aria-hidden className="text-neutral-silver-gray">
          =
        </Typography>
        <Cell label="Pickup" value={format(pickupAt, "h:mm a")} highlight />
      </div>
    )}
  </div>
);
